import type { StationKOT } from '@/types'
import { lockExactPageSize } from '@/lib/printPageSize'
import { printDebug } from '@/lib/printDebug'
import { thermalPrintBaseCss } from '@/lib/thermalPrintCss'
import { desktop, desktopPrint } from '@/lib/printTransport'
import { getStationPrinterConfig, getPrintOffsetCols } from '@/lib/stationPrinterMap'
import {
  bridgePrintJobs,
  isAndroidPrintClient,
  looksLikeNetworkTarget,
  normalizeAgentTarget,
} from '@/lib/bhooklyAppPrint'
import type { CustomerReceiptSettings } from '@/lib/printCustomerReceipt'
import { escapeHtml, kotSlipCss, renderKotSlipHtml } from '@/lib/kotSlip'
import { buildKotEscpos } from '@/lib/escpos/kotEscpos'

/** Opens the browser print dialog for an 80mm-oriented HTML document (thermal / receipt printers). */
export function printThermalHtmlDocument(html: string, paperWidthMm: number = 80): void {
  // Desktop app: print silently to the OS default printer. Browser: off-screen
  // iframe path so no extra popup/window blocks the cashier during service.
  if (desktopPrint(html, { paperWidthMm })) return
  printViaLargeOffscreenIframe(html, paperWidthMm)
}

type KotPrintOpts = {
  paperWidthMm: number
  printableAreaMm: number
  fontScale: number
}

/**
 * The printable HTML document for one or more slips. A slip that carries the
 * structured `slip` renders as semantic HTML (rules are borders, items a
 * grid — nothing can wrap or bleed at any font scale); a bare text payload
 * (older backend, KDS payload) falls back to a <pre>. Exported for tests.
 */
export function buildPrintHtml(slips: StationKOT[], opts: KotPrintOpts): string {
  const body = slips
    .map((k, idx) => {
      const isLast = idx === slips.length - 1
      if (k.slip) return renderKotSlipHtml(k.slip, isLast ? 'kot-slip--last' : '')
      const raw = typeof k.payload === 'string' ? k.payload : JSON.stringify(k.payload, null, 2)
      return `<section class="kot-slip${isLast ? ' kot-slip--last' : ''}"><pre>${escapeHtml(raw)}</pre></section>`
    })
    .join('')

  const baseCss = thermalPrintBaseCss({
    paperWidthMm: opts.paperWidthMm,
    printableAreaMm: opts.printableAreaMm,
    fontScale: opts.fontScale,
  })
  // KOT-specific sizing — chefs read these from across a hot kitchen line.
  // Baseline 14px (vs the receipt's 13px) × kotFontScale (default 1.15 → 16px).
  // Everything inside the slip is in em, so this one value scales the ticket.
  const kotFontPx = Math.round(14 * opts.fontScale * 100) / 100

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>KOT Print</title>
<style>
  ${baseCss}
  body {
    font-family: ui-monospace, 'Cascadia Mono', Consolas, monospace;
    font-size: ${kotFontPx}px;
    font-weight: 700;
    line-height: 1.35;
  }
  pre {
    white-space: pre-wrap;
    margin: 0;
    word-break: break-word;
    font: inherit;
  }
  .kot-slip {
    page-break-inside: avoid;
    break-inside: avoid;
    margin: 0;
    padding: 6px 0;
  }
  /* One print job, multiple "pages" / cuts — thermal drivers vary; separators help either way. */
  .kot-slip:not(.kot-slip--last) {
    page-break-after: always;
    break-after: page;
  }
  .kot-slip + .kot-slip {
    margin-top: 10mm;
    padding-top: 10mm;
    border-top: 2px dashed #000;
  }
  ${kotSlipCss()}
</style></head><body><div class="thermal-page">${body}</div></body></html>`
}

export type KotPrintRouting = {
  /**
   * Print every slip on this one device (OS printer name on desktop, ip:port
   * on Android) instead of each slip's own station printer — the counter's
   * "print here" reprint. Browser path: ignored (the OS dialog picks).
   */
  device?: string
  /** Include KDS-output slips too (a reprint is paper regardless of station type). */
  includeKds?: boolean
}

/** ESC/POS-style text tickets — one print dialog, one slip per station (page breaks + dashed separators). */
export async function printKotReceipts(
  kots: StationKOT[] | undefined,
  cfg: Pick<CustomerReceiptSettings, 'paperWidthMm' | 'printableAreaMm' | 'kotFontScale'>,
  routing: KotPrintRouting = {},
): Promise<void> {
  printDebug.log('kot', 'printKotReceipts() entry', {
    totalKots: kots?.length ?? 0,
    device: routing.device || undefined,
    includeKds: Boolean(routing.includeKds),
  })
  if (!kots?.length) {
    printDebug.log('kot', 'no kots — short-circuit')
    return
  }
  // F-FE-N60: a relayed slip is already queued for the counter terminal to
  // print. Printing it here too would put the same ticket on two printers, so
  // the filter sits ahead of all three transports below rather than inside any
  // one of them. `relayed` is only ever set when THIS client asked to relay.
  const slips = kots.filter(
    (k) => !k.relayed && (routing.includeKds || k.output_type === 'printer'),
  )
  if (slips.length === 0) {
    printDebug.log('kot', 'no local printer-output slips — short-circuit (relayed slips print at the counter)')
    return
  }
  printDebug.log('kot', `building html for ${slips.length} printer slip(s)`)

  // Android only (Server APK / Chrome PWA): ESC/POS via native bridge or
  // intent://. Must NOT run on desktop browser — that used to swallow every
  // counter fire (intent:// no-op, then early return, no window.print()).
  if (isAndroidPrintClient()) {
    const jobs = slips.map((k) => {
      const text =
        typeof k.payload === 'string' ? k.payload : JSON.stringify(k.payload, null, 2)
      const sc = getStationPrinterConfig(k.station_id)
      // Explicit device (print-here reprint), else the machine-local map on
      // this phone, else the backend station printer_target.
      const rawTarget = routing.device || sc.device || k.printer_target || ''
      // F-FE-N111: lay the ticket out for the roll it is actually going to and
      // inset it off both edges — neither of which the agent's own byte wrapper
      // can do. Built from the STRUCTURED slip, so it is the same ticket rather
      // than a re-wrap of the 32-column string. `text` still carries that
      // string, so an agent below 1.3.0 ignores these bytes and prints exactly
      // what it prints today.
      const widthMm = sc.paperWidthMm && sc.paperWidthMm > 0 ? sc.paperWidthMm : cfg.paperWidthMm
      return {
        station: k.station_name,
        target: normalizeAgentTarget(rawTarget),
        text,
        ...(k.slip ? { escpos: buildKotEscpos(k.slip, widthMm, getPrintOffsetCols()) } : {}),
      }
    })
    if (bridgePrintJobs(jobs)) {
      printDebug.log('kot', 'dispatched multi-job Intent to Android agent — skipping browser print')
      return
    }
    printDebug.warn('kot', 'Android intent launch failed — not opening browser print dialog on phone')
    return
  }

  // Electron desktop app: route EACH slip to its station's physical printer.
  if (desktop()) {
    for (const slip of slips) {
      const sc = getStationPrinterConfig(slip.station_id)
      // The DB target is only usable here when it names a WINDOWS printer. An
      // ip:port in that column is meant for the Android agent, and handing it
      // to SumatraPDF as a device name fails the job — so fall through to the
      // OS default instead, which at least prints. See looksLikeNetworkTarget.
      const dbTarget = looksLikeNetworkTarget(slip.printer_target) ? undefined : slip.printer_target
      const device = routing.device || sc.device || dbTarget || undefined
      const widthMm = sc.paperWidthMm && sc.paperWidthMm > 0 ? sc.paperWidthMm : cfg.paperWidthMm
      const printableMm = Math.min(cfg.printableAreaMm, Math.max(40, widthMm - 6))

      const html = buildPrintHtml([slip], {
        paperWidthMm: widthMm,
        printableAreaMm: printableMm,
        fontScale: cfg.kotFontScale,
      })
      desktopPrint(html, {
        deviceName: device,
        paperWidthMm: widthMm,
        printableAreaMm: printableMm,
      })
    }
    return
  }

  // Desktop browser (Chrome/Edge without Electron): OS print / kiosk-printing.
  const html = buildPrintHtml(slips, {
    paperWidthMm: cfg.paperWidthMm,
    printableAreaMm: cfg.printableAreaMm,
    fontScale: cfg.kotFontScale,
  })
  printViaLargeOffscreenIframe(html, cfg.paperWidthMm)
}

/** When popups are blocked: off-screen iframe large enough for full multi-slip layout. */
function printViaLargeOffscreenIframe(html: string, paperWidthMm: number): void {
  const tag = 'kot'
  printDebug.log(tag, 'printViaLargeOffscreenIframe() entry', {
    htmlLength: html.length,
    paperWidthMm,
  })
  const iframe = document.createElement('iframe')
  iframe.setAttribute(
    'style',
    [
      'position:fixed',
      'left:-8000px',
      'top:0',
      'width:320px',
      'min-height:12000px',
      'height:12000px',
      'border:0',
      'opacity:0',
      'pointer-events:none',
      'z-index:-1',
    ].join(';')
  )
  iframe.setAttribute('aria-hidden', 'true')
  document.body.appendChild(iframe)
  printDebug.log(tag, 'iframe appended to body')

  const doc = iframe.contentDocument
  const win = iframe.contentWindow
  if (!doc || !win) {
    printDebug.warn(tag, 'contentDocument/contentWindow is null — aborting', {
      doc: !!doc,
      win: !!win,
    })
    document.body.removeChild(iframe)
    return
  }

  doc.open()
  doc.write(html)
  doc.close()
  printDebug.log(tag, 'html written into iframe doc')
  printDebug.attachWindowProbes(tag, win, doc)

  const runPrint = async () => {
    try {
      await printDebug.spanAsync(tag, 'lockExactPageSize', () =>
        lockExactPageSize(doc, paperWidthMm),
      )
      printDebug.snapshot(tag, 'pre-print snapshot', doc, win)
      win.focus()
      printDebug.span(tag, 'win.print()', () => win.print())
    } catch (err) {
      printDebug.error(tag, 'runPrint threw', err)
    } finally {
      setTimeout(() => {
        printDebug.log(tag, 'cleanup() — removing iframe from DOM (800ms after print fired)')
        try {
          document.body.removeChild(iframe)
        } catch (err) {
          printDebug.warn(tag, 'iframe removal failed', err)
        }
      }, 800)
    }
  }

  requestAnimationFrame(() => {
    setTimeout(runPrint, 300)
  })
}
